import type { BookingDraft, Services, ServiceType, StepsTabs } from '@/scopes/checkout/domain/types'

import { useCMSPageField } from '@/cms/fields'
import { useSendAncillaryAddRemove } from '@/scopes/checkout/domain/analytics/useSendAncillaryAddRemove'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { produce } from 'immer'
import { useSearchParams } from 'next/navigation'
import { compose, filter, map, not, pipe, prop, uniqBy, values } from 'ramda'
import * as React from 'react'
import { useCouponStore } from '../components/CouponCode/store'
import { useEngineErrorContactInfoStore } from '../components/EngineError/store'
import { checkoutAPIClient, serviceMappers } from '../infrastructure/api'
import { useCheckoutContext } from './CheckoutContext'

function updateServiceInBookingDraft<K extends ServiceType>(serviceType: K, service: Services[K]) {
  return produce((currentBookingDraft: BookingDraft) => {
    currentBookingDraft.services[serviceType] = service
  })
}

function updateGenericServiceInBookingDraft<K extends ServiceType>(serviceType: K, service: Services[K]) {
  return produce((currentBookingDraft: BookingDraft) => {
    currentBookingDraft.services[serviceType] = {
      ...(currentBookingDraft.services[serviceType] || {}),
      [service.id]: service,
    }
  })
}

export function useService<K extends ServiceType>(serviceType: K) {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const { sendAncillaryAddRemoveEvent } = useSendAncillaryAddRemove({
    serviceMappers,
  })

  const service = data?.services[serviceType] ?? null

  const setServiceMutation = useMutation({
    mutationFn: async (updatedService: Services[K]) => {
      await checkoutAPIClient.setServiceSelection({ sid, contextItemId }, serviceType, updatedService)
    },
    onMutate: async (updatedService: Services[K]) => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, updateServiceInBookingDraft(serviceType, updatedService))

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async (_, __, context) => {
      if (!service) {
        return
      }
      await queryClient.invalidateQueries({ queryKey })

      const newBookingDraft = queryClient.getQueryData(queryKey)
      if (newBookingDraft) {
        const serviceUpdated = (newBookingDraft as BookingDraft).services[serviceType]
        sendAncillaryAddRemoveEvent(serviceUpdated, serviceType, context?.previousBookingDraft as BookingDraft)
      }
    },
  })

  const setServiceLocalValue = React.useCallback(
    (serviceUpdateFn: (service: Services[K]) => Services[K]) => {
      if (!service)
        return
      const updatedService = serviceUpdateFn(service)
      queryClient.setQueryData(queryKey, updateServiceInBookingDraft(serviceType, updatedService))
    },
    [service, queryClient, queryKey, serviceType],
  )

  return {
    isLoading,
    isFetching,
    service,
    setServiceLocalValue,
    setServiceValue: setServiceMutation.mutate,
  }
}

export function useGenericService<K extends ServiceType>(serviceType: K, type: string) {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const { sendAncillaryGenericServiceAddRemoveEvent } = useSendAncillaryAddRemove({
    serviceMappers,
  })

  const genericTypeService = data?.services[serviceType] ?? null
  const service
    = genericTypeService && type ? (genericTypeService as unknown as Record<string, Services[K]>)[type] : null

  const setServiceMutation = useMutation({
    mutationFn: async (updatedService: Services[K]) => {
      await checkoutAPIClient.setServiceSelection({ sid, contextItemId }, serviceType, updatedService)
    },
    onMutate: async (updatedService: Services[K]) => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, updateGenericServiceInBookingDraft(serviceType, updatedService))

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async (_, __, context) => {
      if (!service) {
        return
      }
      await queryClient.invalidateQueries({ queryKey })
      const newBookingDraft = queryClient.getQueryData(queryKey)
      if (newBookingDraft) {
        const genericService = (newBookingDraft as BookingDraft).services[serviceType]
        const serviceUpdated = (genericService as unknown as Record<string, Services[K]>)[service.id] ?? null
        sendAncillaryGenericServiceAddRemoveEvent(
          serviceUpdated,
          serviceType,
          context?.previousBookingDraft as BookingDraft,
        )
      }
    },
  })

  const setServiceLocalValue = React.useCallback(
    (serviceUpdateFn: (service: Services[K]) => Services[K]) => {
      if (!service)
        return
      const updatedService = serviceUpdateFn(service)
      queryClient.setQueryData(queryKey, updateGenericServiceInBookingDraft(serviceType, updatedService))
    },
    [service, queryClient, queryKey, serviceType],
  )

  return {
    isLoading,
    isFetching,
    service,
    setServiceLocalValue,
    setServiceValue: setServiceMutation.mutate,
  }
}

export function useCheckoutMessage(componentId: string) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,
    message: data?.messages[componentId] ?? null,
  }
}

export function useCheckoutUsps(componentId: string) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,
    data: data?.usps ? data?.usps[componentId] : null,
  }
}

export function usePriceOverviews(componentsId: string[]) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,

    data: data?.priceOverviews
      ? Object.entries(data?.priceOverviews ?? []).find(([key]) => componentsId.includes(key))?.[1]
      : null,
  }
}

const uniqServicesById = uniqBy(prop('id'))
const filterUnselectedServices = filter(compose(not, prop('isSelected')))
const getUnselectedServiceTypes = pipe(
  values,
  filterUnselectedServices,
  uniqServicesById,
  map(prop('id')),
  filter(Boolean),
)

export function useUnselectedServices() {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const unselectedServiceTypes = getUnselectedServiceTypes(data?.services ?? {})

  return {
    isLoading,
    isFetching,
    unselectedServiceTypes,
  }
}

export function useSetParticipantsBadge() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const setParticipantsBadgeSelection = useMutation({
    mutationFn: async (participantsBadge: { id: string, name: string, birthdate?: string }[]) => {
      const payload = participantsBadge.map(({ id, name, birthdate }) => {
        return {
          id,
          name,
          ...(birthdate && { birthdate }),
        }
      })
      await checkoutAPIClient.setParticipantsInformation({ sid, contextItemId }, payload)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    isLoading,
    isFetching,
    setParticipantsBadge: setParticipantsBadgeSelection.mutate,
    participants: data?.participants ?? null,
    rooms: data?.rooms ?? null,
    ageRestrictions: data?.configurations?.ageRestrictions ?? null,
  }
}

export function useCouponCode() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const setCouponInfo = useCouponStore(state => state.setCouponInfo)

  const setCouponCode = useMutation({
    mutationFn: async (couponCode: string) => {
      const response = await checkoutAPIClient.setCouponCode({ sid, contextItemId, couponCode })
      if (response?.couponCodeSuccess) {
        setCouponInfo({
          value: couponCode,
          success: response?.couponCodeSuccess,
        })
      }
      else {
        setCouponInfo({
          value: couponCode,
          success: false,
        })
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    setCouponCode: setCouponCode.mutate,
  }
}

export function useEngineErrorContactInfo() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const setBookingInfoError = useEngineErrorContactInfoStore(state => state.setEngineErrorContactInfoResponse)

  const sendEngineErrorContactInfo = useMutation({
    mutationFn: async (info: { customerName: string, customerEmail?: string, customerPhone?: string }) => {
      const success = await checkoutAPIClient.sendEngineErrorContactInfo({
        sid,
        contextItemId,
        customerName: info.customerName,
        customerEmail: info.customerEmail,
        customerPhone: info.customerPhone,
      })
      setBookingInfoError(success)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    sendEngineErrorContactInfo: sendEngineErrorContactInfo.mutate,
  }
}

export function useBookingDraftQuery() {
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()
  let enableQuery = false
  if (sid) {
    enableQuery = true
  }

  const FF_ACTIVATE_GENERIC_SERVICE_DYNAMIC_STEP_1 = useFeatureFlag('ACTIVATE_GENERIC_SERVICE_DYNAMIC_STEP_1')
  const items = useCMSPageField('Checkout.items')?.items ?? []

  const enableBookingDraftQuery = FF_ACTIVATE_GENERIC_SERVICE_DYNAMIC_STEP_1
    ? enableQuery && Boolean(items)
    : enableQuery

  const bookingDraftQuery = useQuery<BookingDraft>({
    queryKey,
    queryFn: async () => {
      if (FF_ACTIVATE_GENERIC_SERVICE_DYNAMIC_STEP_1) {
        return checkoutAPIClient.getBookingDraft(
          {
            sid,
            contextItemId,
          },
          {
            configData: items ?? [],
          },
        )
      }
      return checkoutAPIClient.getBookingDraft({
        sid,
        contextItemId,
      })
    },
    enabled: enableBookingDraftQuery,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    retry: false,
  })

  return {
    ...bookingDraftQuery,
  }
}

export function useBookingDraftQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['booking-draft', sid], [sid])
}

export function useGetStepsTabs() {
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const { isLoading, isFetching, data } = useQuery<StepsTabs>({
    queryKey: ['booking-steps-tabs', sid],
    queryFn: async () =>
      checkoutAPIClient.getStepsTabs({
        sid,
        contextItemId,
      }),
    retry: false,
  })

  return {
    isLoading,
    isFetching,
    activeStep: data?.steps.activeStep ?? '',
    items: data?.steps.items ?? [],
  }
}

export function useCheckoutSid() {
  const searchParams = useSearchParams()
  return React.useMemo(() => searchParams.get('sid') ?? '', [searchParams])
}
